import { useRef, useEffect } from 'react';
import { css } from '@emotion/react';

import { Description, Text, Container } from '@toolbox/components';
import { makeStyles } from '@toolbox/style';
import { useWidgetContext } from '@widget/core';
import { NavButton } from '../NavButton';
import { ActionButton } from '../ActionButton';
import { QuestionHeader } from '../QuestionHeader';
import { SocialProof } from '../SocialProof/SocialProof';
import { LAYOUT_HEADER_TYPE } from '@widget/storyblok/contants';

import type { QuestionLayoutProps } from './types';
import type { ReactFCWithChildren } from '@widget/types/ReactFCWithChildren';

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    color: ${theme.palette.colors.primary};
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    ${theme.media.up('sm')} {
      padding: 0;
      overflow: hidden;
      height: 100%;
    }
  `,

  grid: css`
    width: 100%;
    margin: 0 auto;
    flex-direction: column;
    display: flex;
    justify-content: space-between;
    align-items: space-between;
    height: 100%;
  `,
  outerGridItem: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
  `,
  gridItem: css`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,
  textContainer: css`
    padding-bottom: ${theme.spacing(4)};
  `,
  socialProof: css`
    background-color: ${theme.palette.colors.primary};
    color: ${theme.palette.colors.white};
    padding: 6px 12px;
    margin: 0 auto 1rem;
    border-radius: 50px;
    text-align: center;

    p {
      font-size: 14px;
    }
  `,
  backBtn: css`
    color: ${theme.palette.colors.gray[400]};

    &.tertiary {
      padding: ${theme.spacing(0, 4)};
    }
  `,
  subTitle: css`
    color: ${theme.palette.colors.gray[400]};
    font-weight: ${theme.fontWeights.medium};
  `,
  description: css`
    font-weight: ${theme.fontWeights.medium};
  `,
  options: css`
    margin: ${theme.spacing(8, 0, 4)};

    ${theme.media.down('sm')} {
      margin: ${theme.spacing(0)};
    }
  `,
  optionsMini: css`
    margin: 0;
  `,
  trustIcons: css`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(10)};

    ${theme.media.up('sm')} {
      display: none;
    }
  `,
  footer: css`
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    margin-top: auto;
    border-top: 1px solid ${theme.palette.colors.petrol[400]};
    background: white;
    padding: ${theme.spacing(2)};
    backdrop-filter: blur(10px);
    z-index: 2;
    background-color: rgba(255, 255, 255, 0.6);

    ${theme.media.up('sm')} {
      background-color: transparent;
      background: transparent;
      min-height: 3rem;
      z-index: 0;
      padding: 0;
      position: static;
      border: none;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      margin-top: ${theme.spacing(0)};
    }
  `,
  submitBtn: css`
    min-width: 160px;

    ${theme.media.up('sm')} {
      min-width: 200px;
    }
  `,
  enabled: css`
    color: ${theme.palette.colors.primary};
  `,
  skipBtn: css`
    display: none;

    ${theme.media.up('sm')} {
      display: block;
      min-width: 200px;
    }
  `,
  form: css`
    display: flex;
    flex-grow: 1;
  `,
}));

export const QuestionLayout: ReactFCWithChildren<QuestionLayoutProps> = ({
  className,
  backButtonText = 'Zurück',
  submitButtonText = 'Weiter',
  skipButtonText = 'Überspringen',
  back,
  submit,
  skip,
  title,
  subTitle,
  description,
  children,
  maxW = 'xl',
  socialProofMessage,
  preventAutoFocus,
  headerType,
  mode = 'default',
  showProgressBar,
}) => {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const { activeId, getStepProps, allStepsPreviewMode } = useWidgetContext();

  useEffect(() => {
    if (allStepsPreviewMode || preventAutoFocus || getStepProps(activeId)?.disableAutofocus) {
      return;
    }

    const firstTextInput = ref.current?.querySelector<HTMLInputElement>(
      'input[type="text"], input:not([type])',
    );

    if (firstTextInput) {
      setTimeout(() => {
        firstTextInput.focus();
      }, 700);
    }
  }, [activeId, getStepProps, preventAutoFocus]);

  return (
    <Container css={[classes.root as any]} className={className} maxW={maxW}>
      <div css={classes.grid as any}>
        <div css={classes.outerGridItem as any}>
          <QuestionHeader title={title} showProgressBar={showProgressBar} />

          <div css={classes.gridItem as any}>
            {(subTitle || description || socialProofMessage) && (
              <div css={classes.textContainer as any}>
                {subTitle && (
                  <Text variant="b2" css={classes.subTitle as any} mb={1}>
                    {subTitle}
                  </Text>
                )}
                {description && <Description variant="b2">{description}</Description>}
                {socialProofMessage && headerType !== LAYOUT_HEADER_TYPE.SOCIAL_PROOF_MESSAGE && (
                  <SocialProof message={socialProofMessage} mode={mode} />
                )}
              </div>
            )}
            <div ref={ref} css={[classes.options, mode === 'mini' && classes.optionsMini] as any[]}>
              {children}
            </div>
          </div>
        </div>
        {(back || submit) && (
          <div css={classes.footer as any}>
            {back && (
              <NavButton
                css={[classes.backBtn, back && classes.enabled] as any}
                variant="tertiary"
                back
              >
                &lt; {backButtonText}
              </NavButton>
            )}
            {skip && (
              <ActionButton skip css={classes.skipBtn as any} size="medium" variant="tertiary">
                {skipButtonText}
              </ActionButton>
            )}
            {submit && (
              <ActionButton submit css={classes.submitBtn as any} size="medium">
                {submitButtonText}
              </ActionButton>
            )}
          </div>
        )}
      </div>
    </Container>
  );
};
